import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { Building2, Users, FolderKanban, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, compRes] = await Promise.all([
          api.superadminGetStats(),
          api.superadminGetCompanies(),
        ]);
        setStats(statsRes.data);
        setCompanies(compRes.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-[hsl(16,100%,55%)] border-t-transparent op-round rounded-full animate-spin" />
    </div>
  );

  const cards = [
    { label: 'Företag', value: stats?.total_companies || 0, icon: Building2 },
    { label: 'Användare', value: stats?.total_users || 0, icon: Users },
    { label: 'Projekt', value: stats?.total_projects || 0, icon: FolderKanban },
    { label: 'Aktiva prenumerationer', value: stats?.active_subscriptions || 0, icon: CreditCard },
  ];

  return (
    <div className="space-y-6" data-testid="superadmin-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={i} className="bg-white border border-border rounded p-5" data-testid={`sa-stat-${i}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{c.label}</p>
                  <p className="text-3xl font-bold tracking-tight text-foreground mt-1">{c.value}</p>
                </div>
                <div className="p-2 bg-muted rounded">
                  <Icon size={18} className="text-muted-foreground" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-border rounded" data-testid="sa-companies-table">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-base font-bold tracking-tight">Registrerade företag</h2>
          <Link to="/superadmin/companies" className="text-xs text-[hsl(16,100%,55%)] hover:underline">Visa alla</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Företag</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Org.nr</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Användare</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Projekt</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Prenumeration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {companies.filter(c => c.name !== 'Operio System').slice(0, 10).map((c) => (
                <tr key={c.id} data-testid={`sa-company-${c.id}`}>
                  <td className="px-5 py-3 text-sm font-medium text-foreground">{c.name}</td>
                  <td className="px-5 py-3 text-sm text-muted-foreground">{c.org_number || '—'}</td>
                  <td className="px-5 py-3 text-sm">{c.user_count}</td>
                  <td className="px-5 py-3 text-sm">{c.project_count}</td>
                  <td className="px-5 py-3 text-sm">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                      c.subscription ? 'bg-green-50 text-green-700' : 'bg-muted text-muted-foreground'
                    }`}>
                      {c.subscription ? c.subscription.status : 'Ingen'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
